import { useMemo } from 'react'

import { getCurrentAppId, isAppId, isAivard, isYagel } from '../utils'

/**
 * React hook for working with APP_ID
 * @returns {object} Object with APP_ID utilities
 */
export const useAppId = () => {
  const appId = useMemo(() => getCurrentAppId(), [])

  return useMemo(() => ({
    appId,
    isAivard: isAivard(),
    isYagel: isYagel(),
    isAppId: (id: string) => isAppId(id),
    // Helper for conditional rendering
    renderByAppId: <T extends Record<string, any>>(
      configs: T,
      defaultConfig?: T[keyof T]
    ): T[keyof T] => {
      return configs[appId] || defaultConfig || configs['aivard']
    }
  }), [appId])
}

/**
 * Hook for conditional rendering based on APP_ID
 * @param {Record<string, React.ReactNode>} components - Object with app-specific components
 * @param {React.ReactNode} defaultComponent - Default component to render
 * @returns {React.ReactNode} Component for current APP_ID or default
 */
export const useAppSpecificComponent = (
  components: Record<string, React.ReactNode>,
  defaultComponent?: React.ReactNode
): React.ReactNode => {
  const { appId } = useAppId()

  return useMemo(() => {
    return components[appId] || defaultComponent || components['aivard']
  }, [appId, components, defaultComponent])
}

/**
 * Hook for app-specific configuration
 * @param {Record<string, T>} configs - Object with app-specific configurations
 * @param {T} defaultConfig - Default configuration
 * @returns {T} Configuration for current APP_ID or default
 */
export const useAppSpecificConfig = <T>(
  configs: Record<string, T>,
  defaultConfig?: T
): T => {
  const { appId } = useAppId()

  return useMemo(() => {
    return configs[appId] || defaultConfig || configs['aivard']
  }, [appId, configs, defaultConfig])
}
